import { get } from 'lodash';
import {
  convertObjToCamelCase,
  retrieveDataMapper,
  retrieveObjectMapper,
} from '../..';

export const transformFindAllCentreForSchool = (
  { isFetchList = false, onlyIncludeAddresses = false },
  ...responses
) => {
  const [
    resp,
    users,
    centreAddresses,
    centreLevels,
    centreNearbyPostcodes,
    centreLevelPrograms,
    centreWorkplaces,
    billableItems,
    linkpointAwardConfigs = [],
  ] = responses;
  let fields = ['ID:id', 'code', 'label', 'updated_at', 'active'];
  if (!isFetchList) {
    fields = [
      ...fields,
      'ecda_code',
      'cost_centre_code',
      'fk_primary_email_contact',
      'email',
      'fk_school',
      'fk_govt_scheme',
      'fk_certification',
      'teacher_ratio',
      'created_at',
      'link_branch_id',
      'spark_expiration_date',
      'first_licence_date',
      'license_duration',
      'first_operation_date',
      'license_renewal_date',
      'license_renewal_duration',
      'licensed_infant_care_capacity',
      'licensed_childcare_capacity',
      'is_public',
      'enrolment_dates_setting',
      'to',
      'department_centre',
    ];
  }

  return {
    success: true,
    data: {
      findAllCentreForSchool: {
        data: (get(resp, 'data', []) || []).map(data => {
          const centreId = get(data, 'id');
          return {
            ...retrieveObjectMapper(data, fields),
            ...(isFetchList && {
              userByLastUpdatedByFkUser: retrieveDataMapper(
                users,
                data?.last_updated_by_fk_user,
                ['firstname', 'lastname']
              ),
            }),
            ...((!isFetchList || onlyIncludeAddresses) && {
              addresses: {
                data: (get(centreAddresses, `${centreId}.data`) || [])?.map(
                  item =>
                    retrieveObjectMapper(item, [
                      'ID:id',
                      'name',
                      'floor',
                      'unit',
                      'address',
                      'building',
                      'city',
                      'postcode',
                      'lat',
                      'lng',
                    ])
                ),
                totalCount: get(centreAddresses, `${centreId}.totalCount`, 0),
              },
            }),
            ...(!isFetchList &&
              !onlyIncludeAddresses && {
                centreWorkplaces: {
                  data: (
                    get(centreWorkplaces, `${centreId}.data`, []) || []
                  )?.map(ctw =>
                    retrieveObjectMapper(ctw, [
                      'ID:id',
                      'fk_workplace',
                      'fk_centre',
                    ])
                  ),
                  totalCount: get(
                    centreWorkplaces,
                    `${centreId}.totalCount`,
                    0
                  ),
                },
                centreLevels: {
                  data: (get(centreLevels, `${centreId}.data`) || [])?.map(
                    item =>
                      retrieveObjectMapper(item, [
                        'ID:id',
                        'fk_level',
                        'auto_promotion_flg',
                        'auto_promotion_job_day',
                        'auto_promotion_job_month',
                        'reminder_days',
                        'created_at',
                        'updated_at',
                      ])
                  ),
                  totalCount: get(centreLevels, `${centreId}.totalCount`, 0),
                },
                centreNearbyPostcodes: (() => {
                  const value = get(centreNearbyPostcodes, `${centreId}`);
                  return {
                    data: (get(value, 'data') || [])?.map(ctn =>
                      retrieveObjectMapper(ctn, [
                        'ID:id',
                        'fk_centre',
                        'postcode',
                        'created_at',
                        'updated_at',
                      ])
                    ),
                    totalCount: get(value, 'totalCount', 0),
                  };
                })(),
                centreLevelPrograms: {
                  data: (centreLevelPrograms || [])
                    ?.filter(flt => flt?.fk_centre === centreId)
                    ?.map(ctrLv =>
                      retrieveObjectMapper(ctrLv, ['fk_level', 'fk_program'])
                    ),
                },
                linkpointAwardConfigs: {
                  data: (linkpointAwardConfigs || []).map(config =>
                    convertObjToCamelCase(
                      config,
                      [],
                      [
                        'ID:id',
                        'operatorID:operator_id',
                        'terminalID:terminal_id',
                        'merchantID:merchant_id',
                      ]
                    )
                  ),
                },
                billableItems: {
                  data: (get(billableItems, 'data') || [])?.map(billableItem =>
                    retrieveObjectMapper(billableItem, [
                      'unit_price',
                      'type:type_string',
                    ])
                  ),
                  totalCount: get(billableItems, 'totalCount', 0),
                },
              }),
          };
        }),
        totalCount: get(resp, 'totalCount', 0),
      },
    },
  };
};

export const transformCentreDocuments = (documents = [], usersMap = {}) => {
  return documents.map(doc => ({
    ...retrieveObjectMapper(doc, [
      'ID:id',
      'fk_centre',
      'fk_centre_document_category',
      'uRL:url',
      'filename',
      'remark',
      'active',
      'created_at',
      'updated_at',
    ]),
    preSignedUrl: get(doc, 'pre_signed_url') || get(doc, 'url'),
    user: usersMap[doc.fk_user]
      ? retrieveObjectMapper(usersMap[doc.fk_user], [
          'firstname',
          'lastname',
          'email',
        ])
      : {},
  }));
};

export const transformCentreActivityLogs = (logs = [], usersMap = {}) => {
  return logs.map(log => {
    const mappedLog = retrieveObjectMapper(log, [
      'ID:id',
      'createdAt:created_at',
      'updatedAt:updated_at',
      'oldData:old_data',
      'updatedData:updated_data',
      'changeType:change_type',
      'changeTypeKey:change_type_key',
    ]);
    return {
      ...mappedLog,
      oldData: get(mappedLog, 'oldData') || '',
      updatedData: get(mappedLog, 'updatedData') || '',
      changeType: get(mappedLog, 'changeType') || '',
      changeTypeKey: get(mappedLog, 'changeTypeKey') || '',
      user: usersMap[log.fk_user]
        ? retrieveObjectMapper(usersMap[log.fk_user], [
            'ID:id',
            'firstname',
            'lastname',
            'email',
          ])
        : {
            ID: log.fk_user || null,
            firstname: '',
            lastname: '',
            email: '',
          },
    };
  });
};

export default {
  transformFindAllCentreForSchool,
  transformCentreDocuments,
  transformCentreActivityLogs,
};
